/**
 * Admin Event Exchange API — `/admin/exchange/...` on the px backend.
 *
 *   currencies   global list (PX, USDT, BTC ...) with the "where to send" guide
 *   rates        what each currency costs in one event's points, and its limits
 *   orders       the review queue, PX included. Approve credits PX, or records
 *                the TXID of crypto that was sent. Reject refunds the points
 *                while the event is still running. Revert undoes either.
 */

const getApiBase = () => {
  const base = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
  return base.endsWith("/admin") ? `${base}/exchange` : `${base}/admin/exchange`;
};

const headers = () => {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const handleResponse = async (response) => {
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data.status === false) {
    const err = new Error(data?.message || data?.error || `Request failed with status ${response.status}`);
    err.response = data;
    throw err;
  }
  return data;
};

const request = async (method, path, body) => {
  const res = await fetch(`${getApiBase()}${path}`, {
    method,
    headers: headers(),
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  return handleResponse(res);
};

// ── currencies ───────────────────────────────────────────────────────────────
export const fetchExchangeOptions = () => request("GET", "/options");
export const createExchangeOption = (body) => request("POST", "/options", body);
export const updateExchangeOption = (id, body) => request("PUT", `/options/${encodeURIComponent(id)}`, body);
export const deleteExchangeOption = (id) => request("DELETE", `/options/${encodeURIComponent(id)}`);

// ── per-event rates ──────────────────────────────────────────────────────────
export const fetchEventExchangeRates = (eventId) => request("GET", `/event/${eventId}/rates`);
export const saveEventExchangeRate = (eventId, optionId, body) =>
  request("PUT", `/event/${eventId}/rates/${encodeURIComponent(optionId)}`, body);
/** Drop the event's own rate: back to the currency default, or not offered when it has none. */
export const deleteEventExchangeRate = (eventId, optionId) =>
  request("DELETE", `/event/${eventId}/rates/${encodeURIComponent(optionId)}`);
export const saveEventExchangeSettings =(eventId, body) => request("PUT", `/event/${eventId}/settings`, body);

// ── orders ───────────────────────────────────────────────────────────────────
export const fetchExchangeTransactions = ({ status = "All", symbol = "", search = "", eventId = "", page = 1, limit = 50 } = {}) => {
  const query = new URLSearchParams();
  if (status) query.append("status", status);
  if (symbol) query.append("symbol", symbol);
  if (search) query.append("search", search.trim());
  if (eventId) query.append("event_id", eventId);
  query.append("page", page);
  query.append("limit", limit);
  return request("GET", `/transactions?${query.toString()}`);
};
export const approveExchangeOrder = (orderId, body) =>
  request("POST", `/transactions/${encodeURIComponent(orderId)}/approve`, body);
export const rejectExchangeOrder = (orderId, body) =>
  request("POST", `/transactions/${encodeURIComponent(orderId)}/reject`, body);
/** Undo an approve or reject: the order goes back to Pending. */
export const revertExchangeOrder = (orderId, body) =>
  request("POST", `/transactions/${encodeURIComponent(orderId)}/revert`, body);
